import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    TablePagination,
    TextField,
    MenuItem,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Chip,
} from '@mui/material';
import { ArrowDownward as ArrowDownwardIcon, ArrowUpward as ArrowUpwardIcon } from '@mui/icons-material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { reportService } from '../../services/api';
import { formatDate } from '../../utils/dateFormatter';
import { useToast } from '../../context/ToastContext';
import ResponsiveTable from '../common/ResponsiveTable';
import { tokens } from '../../theme';
import StatusPill from '../shared/StatusPill';

// Active-user report.
// One row per user, so the headline count is auditable: the number of "Yes"
// rows equals the active count. Active = logged in during the period
// (users.last_login). Login-based, never per-search.
// Set showTitle={false} when the host page already renders its own heading.

const PERIOD_OPTIONS = [
    { value: 'all', label: 'All time' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },    
];

const AUDIENCE_OPTIONS = [
    { value: 'all', label: 'All audiences' },
    { value: 'internal', label: 'Internal' },
    { value: 'external', label: 'External' },
];

const ActiveUsersReport = ({ showTitle = true }) => {
    const T = tokens;
    const { showToast } = useToast();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState([]);
    const [summary, setSummary] = useState(null);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [filters, setFilters] = useState({ period: 'all', audience: 'all', activeOnly: false });
    const [sortDirection, setSortDirection] = useState('desc'); // by last active
    const requestId = useRef(0);

    const updateFilters = (patch) => {
        setFilters(prev => ({ ...prev, ...patch }));
        setPage(0);
    };

    const handleSort = () => {
        setSortDirection(prev => (prev === 'desc' ? 'asc' : 'desc'));
        setPage(0);
    };

    // Same sortable-header treatment as ComplianceReport.
    const sortableHeader = (label) => (
        <Box
            component="span"
            onClick={handleSort}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                cursor: 'pointer',
                userSelect: 'none',
                color: T.color.textPrimary,
                fontWeight: T.font.weight.medium,
                '&:hover': { color: T.color.textPrimary },
            }}
        >
            {label}
            {sortDirection === 'desc'
                ? <ArrowDownwardIcon sx={{ fontSize: '13px' }} />
                : <ArrowUpwardIcon sx={{ fontSize: '13px' }} />}
        </Box>
    );

    const columns = [
        {
            id: 'name',
            label: 'Name',
            minWidth: 190,
            format: (value) => (
                <Typography sx={{ fontSize: T.font.size.md, fontWeight: T.font.weight.medium, color: T.color.textPrimary }}>
                    {value || '-'}
                </Typography>
            ),
        },
        { id: 'email', label: 'Email', minWidth: 220 },
        { id: 'audience', label: 'Audience', minWidth: 100 },
        {
            id: 'lastActive',
            label: sortableHeader('Last active'),
            minWidth: 130,
            format: (value) => (value ? formatDate(value) : 'Never'),
        },
        {
            id: 'activeVia',
            label: 'Active',
            minWidth: 100,
            format: (value) => (
                <Chip
                    label={value}
                    size="small"
                    sx={{
                        backgroundColor: value === 'Never' ? T.color.bgTertiary : T.color.bgSecondary,
                        color: value === 'Never' ? T.color.textSecondary : T.color.textPrimary,
                        borderRadius: T.radius.pill,
                        height: 24,
                    }}
                />
            ),
        },
        {
            id: 'activeThisPeriod',
            label: 'Active this period?',
            minWidth: 150,
            format: (value) => (                
                <StatusPill status={value ? 'Yes' : 'No'} />
            ),
        },
    ];

    useEffect(() => {
        const id = ++requestId.current;
        setLoading(true);
        reportService
            .getActiveUsersReport({
                ...filters,
                sort: sortDirection,
                page: page + 1, // API pages are 1-based; TablePagination is 0-based
                limit: rowsPerPage,
            })
            .then(response => {
                if (id !== requestId.current) return; // a newer request superseded this one
                if (!response.data.success) throw new Error(response.data.message || 'Request failed');
                setData(response.data.data.rows);
                setSummary(response.data.data.summary);
                setTotal(response.data.data.total);
            })
            .catch(error => {
                if (id !== requestId.current) return;
                console.error('Error fetching active users report:', error);
                showToast('Failed to load the active users report', 'error');
            })
            .finally(() => {
                if (id === requestId.current) setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, sortDirection, page, rowsPerPage]);

    const handleExport = async () => {
        try {
            setExporting(true);
            const response = await reportService.exportActiveUsersReport({ ...filters, sort: sortDirection });
            const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `active-users-${filters.period}-${filters.audience}-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting active users report:', error);
            showToast('Failed to export the report', 'error');
        } finally {
            setExporting(false);
        }
    };

    const activeCount = summary ? summary.activeCount : 0;
    const totalCount = summary ? summary.totalCount : 0;
    const inactiveCount = Math.max(totalCount - activeCount, 0);
    const activePct = totalCount ? (activeCount / totalCount) * 100 : 0;
    const sinceLabel = summary
        ? (summary.periodStart ? `Logged in since ${formatDate(summary.periodStart)}` : 'Logged in at least once')
        : '';

    return (
        <Box sx={{ p: 2 }}>
            {showTitle && (
                <Typography variant="h4" gutterBottom className="page-title">
                    Active Users Report
                </Typography>
            )}
            <Typography variant="body2" sx={{ mb: 4 }}>
                For 7/30/90 days, active means logged in or ran a search in that window.
                For All time, active means an enabled learner account.
            </Typography>

            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        select
                        className="pink-filter"
                        variant="outlined"
                        size="small"
                        label="Period"
                        value={filters.period}
                        onChange={(e) => updateFilters({ period: e.target.value })}
                        sx={{ minWidth: 150 }}
                    >
                        {PERIOD_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        className="pink-filter"
                        variant="outlined"
                        size="small"
                        label="Audience"
                        value={filters.audience}
                        onChange={(e) => updateFilters({ audience: e.target.value })}
                        sx={{ minWidth: 150 }}
                    >
                        {AUDIENCE_OPTIONS.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                    <FormControlLabel
                        control={
                            <Checkbox
                                size="small"
                                checked={filters.activeOnly}
                                onChange={(e) => updateFilters({ activeOnly: e.target.checked })}
                            />
                        }
                        label={<Typography sx={{ fontSize: T.font.size.md, color: T.color.textPrimary }}>Active users only</Typography>}
                    />
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<FileDownloadIcon />}
                    onClick={handleExport}
                    disabled={exporting || total === 0}
                >
                    {exporting ? 'Exporting...' : 'Export'}
                </Button>
            </Box>

            {/* ── Active users overview card ─────────────────────────────────── */}
            <Paper
                sx={{
                    mb: 3,
                    p: 3,
                    background: T.color.bgPrimary,
                    border: `0.5px solid ${T.color.borderTertiary}`,
                    borderRadius: T.radius.lg,
                }}
            >
                <Typography
                    sx={{
                        fontSize: T.font.size.xs,
                        color: T.color.textSecondary,
                        letterSpacing: T.font.letterSpacing.label,
                        textTransform: 'uppercase',
                        mb: 2,
                    }}
                >
                    Active users overview
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                    {/* Big number */}
                    <Box sx={{ minWidth: '120px' }} aria-live="polite">
                        <Typography sx={{ fontSize: '40px', fontWeight: 700, lineHeight: 1, color: T.color.status.activeText }}>
                            {summary ? activeCount : '-'}
                        </Typography>
                        <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, mt: 1 }}>
                            active users
                        </Typography>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ width: '0.5px', alignSelf: 'stretch', backgroundColor: T.color.borderTertiary, display: { xs: 'none', md: 'block' } }} />

                    {/* Bar + legend */}
                    <Box sx={{ flex: 1, minWidth: '260px' }}>
                        <Box sx={{ display: 'flex', height: '30px', borderRadius: T.radius.sm, overflow: 'hidden', background: T.color.bgTertiary }}>
                            <Box sx={{ width: `${activePct}%`, background: T.color.status.activeText }} />
                        </Box>

                        <Box sx={{ display: 'flex', gap: 4, mt: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '4px', background: T.color.status.activeText }} />
                                <Typography sx={{ fontSize: T.font.size.md, color: T.color.textPrimary }}>
                                    Active <Box component="span" sx={{ color: T.color.textSecondary }}>{summary ? activeCount : '-'}</Box>
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Box sx={{ width: 12, height: 12, borderRadius: '4px', background: T.color.bgTertiary, border: `0.5px solid ${T.color.borderSecondary}` }} />
                                <Typography sx={{ fontSize: T.font.size.md, color: T.color.textPrimary }}>
                                    Not active <Box component="span" sx={{ color: T.color.textSecondary }}>{summary ? inactiveCount : '-'}</Box>
                                </Typography>
                            </Box>
                        </Box>

                        {summary && (
                            <Typography sx={{ fontSize: T.font.size.sm, color: T.color.textSecondary, mt: 2 }}>
                                {activeCount} of {totalCount} users &middot; {sinceLabel}. Equals the number of &ldquo;Yes&rdquo; rows below.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* ── Users table card ───────────────────────────────────────────── */}
            <Paper
                sx={{
                    width: '100%',
                    overflow: 'hidden',
                    borderRadius: T.radius.lg,
                    '& .MuiTableRow-root:nth-of-type(even)': {
                        backgroundColor: T.color.bgSecondary,
                    },
                }}
            >
                <Box sx={{ px: 3, pt: 3, pb: 1 }}>
                    <Typography
                        sx={{
                            fontSize: T.font.size.xs,
                            color: T.color.textSecondary,
                            letterSpacing: T.font.letterSpacing.label,
                            textTransform: 'uppercase',
                        }}
                    >
                        Users &middot; Sortable by last active
                    </Typography>
                </Box>
                <ResponsiveTable
                    columns={columns}
                    rows={data}
                    loading={loading}
                    emptyTitle={
                        filters.activeOnly
                            ? 'No users were active in this period'
                            : 'No users match these filters'
                    }
                    renderCell={(column, row) => {
                        const value = row[column.id];

                        if (column.format && typeof value !== 'undefined') {
                            return column.format(value, row);
                        }

                        return value ?? '-';
                    }}
                />
                <TablePagination
                    rowsPerPageOptions={[10, 25, 100]}
                    component="div"
                    count={total}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                        setRowsPerPage(+event.target.value);
                        setPage(0);
                    }}
                />
            </Paper>
        </Box>
    );
};

export default ActiveUsersReport;